import * as _ from "lodash";
import * as moment from "moment-timezone";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import {
  getDeliveryCutoff,
  getDeliveryWeekLeadTimes
} from "src/core/drops/utils/inherited-settings";
import { useDrop } from "src/core/drops/hooks";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import useMinimumLeadTime from "src/core/shared/hooks/useMinimumLeadTime";
import { IOrder } from "src/core/orders/api/orders.api";
import {
  IDrop,
  WeekDay
} from "src/core/drops/api/drops.api";
import { IProductLeadTimeGroup } from "src/core/products/api/products.api";
import { useProductLeadTimeGroup } from "src/core/products/hooks/useProductLeadTimeGroup";
import { hourMinuteTimeSplit } from "src/library/utils/time-helpers/time-helpers";
import { useShutdownLeadTimeExtend } from "src/modules/shutdowns/hooks/useShutdownLeadTimeExtend";
import { completeLeadTimeConfiguration } from "src/core/orders/utils/order-deliveries";
import { IStore } from "src/core/shared/store";

import { useLeadTimeOverrideCutoff } from "./useLeadTimeOverrideCutoff";

/**
 * Get number of days left for an order cutoff.
 *
 * @param {IOrder} order - order object.
 * @param {IDeliveryCutoffSetting} partOrder
 *
 * @return - Whether the date has passed or not.
 */
const useOrderCutoffTime = (inputDrop?: IDrop) => {
  const [ { drop } ] = useDrop();

  const dropToUse = React.useMemo(() => {
    if (inputDrop) {
      return inputDrop;
    }

    return drop;
  }, [ drop, inputDrop ]);

  const { minimumLeadTime } = useMinimumLeadTime();
  const getShutdownsExtendDays = useShutdownLeadTimeExtend();
  const timezone = useTimezone();
  const productLeadTimeGroup = useProductLeadTimeGroup();
  const getLeadTimeOverride = useLeadTimeOverrideCutoff();

  const { orderVariants, productLeadTimeGroups } = useMappedState((store: IStore) => ({
    orderVariants: store.global?.settings?.orderVariants || [],
    productLeadTimeGroups: store.global.settings?.leadTimeGroups || []
  }));

  const orderCutOffTime = React.useCallback(
    (order: IOrder, shortLong: "shortest" | "nearest" |"longest" = "shortest"): Date | null => {
      if (!dropToUse) {
        return null;
      }

      let leadTimes = getDeliveryWeekLeadTimes(dropToUse.groupedSettings);
      const defaultCutoff = getDeliveryCutoff(dropToUse.groupedSettings);

      if (!leadTimes || !defaultCutoff) {
        return null;
      }

      // Only do delivery date validation if it's a date-led order
      if (order.orderDeliveryDate) {
        const leadTimeOverride = getLeadTimeOverride(order.orderDeliveryDate);

        if (leadTimeOverride) {
          return leadTimeOverride;
        }

        const orderTypeLeadTimeId = _.find(orderVariants, ov => ov.id === order.orderVariantId)?.defaultProductLeadTimeGroupId;
        const minLeadTimeByType = _.find(productLeadTimeGroups, pltg => pltg.id === orderTypeLeadTimeId) || minimumLeadTime;
        let orderLeadTimes: IProductLeadTimeGroup[] = minLeadTimeByType ? [ minLeadTimeByType ] : [];

        _.forEach(order.orderLines, ol => {
          const productLeadTime = productLeadTimeGroup(ol.product);

          if (productLeadTime) {
            orderLeadTimes.push(productLeadTime);
          }
        });
        orderLeadTimes = _.uniqBy(_.compact(orderLeadTimes), "name");

        let orderCutoff = [ "shortest", "nearest" ].includes(shortLong) ? moment.tz(order.orderDeliveryDate, timezone).subtract(minimumLeadTime?.defaultLeadTimeDays || 0) : moment.tz(timezone);

        leadTimes = completeLeadTimeConfiguration(orderLeadTimes, leadTimes);
        let hasBeenSet = false;

        for (const leadTime of orderLeadTimes) {
          // get the cutoff time for this lead time
          const deliverydateMoment = moment.tz(order.orderDeliveryDate, timezone);
          const deliveryDOW: WeekDay = deliverydateMoment.isoWeekday() as WeekDay;
          const deliveryDOWString = deliveryDOW.toString();
          // get the day the cutt of set from
          const orderByDay = leadTimes.deliveryWeekLeadTimes[ leadTime.name ][ deliveryDOWString ].day;
          const orderByWeek = leadTimes.deliveryWeekLeadTimes[ leadTime.name ][ deliveryDOWString ].week || 0;
          const cutOffTime = leadTimes.deliveryWeekLeadTimes[ leadTime.name ][ deliveryDOWString ].cutoffTime;
          const leadTimeCalc = (deliveryDOW - orderByDay + 7) % 7;
          const deliveryCutOff = deliverydateMoment.clone().subtract(leadTimeCalc, "days");

          deliveryCutOff.subtract(orderByWeek, "week");

          if (!cutOffTime) {
            return null;
          }
          const { hours, minutes } = hourMinuteTimeSplit(cutOffTime);

          deliveryCutOff.hours(hours).minutes(minutes);
          const leadTimeCutoff = deliveryCutOff;
          const daysToExtend = getShutdownsExtendDays(order.orderDeliveryDate, deliveryCutOff.toDate(), order.orderVariantId);

          deliveryCutOff.subtract(daysToExtend, "day");

          // return the shortest or longets depending on setting
          if (shortLong === "shortest") {
            if (leadTimeCutoff.isSameOrBefore(orderCutoff, "minutes")) {
              orderCutoff = leadTimeCutoff;
              hasBeenSet = true;
            }
          } else if (shortLong === "longest") {
            if (hasBeenSet) {
              if (leadTimeCutoff.isSameOrAfter(orderCutoff, "minutes")) {
                orderCutoff = leadTimeCutoff;
                hasBeenSet = true;
              }
            } else {
              orderCutoff = leadTimeCutoff;
              hasBeenSet = true;
            }
          } else if (shortLong === "nearest") {
            if (moment.utc().isBefore(leadTimeCutoff)) {
              if (leadTimeCutoff.isSameOrBefore(orderCutoff, "minutes")) {
                orderCutoff = leadTimeCutoff;
                hasBeenSet = true;
              }
            }
          }
        }

        if (hasBeenSet) {
          return orderCutoff.toDate();
        } else if (shortLong === "shortest") {
          return orderCutoff.toDate();
        }
      }

      return null;
    },
    [
      dropToUse,
      minimumLeadTime,
      timezone,
      getShutdownsExtendDays,
      productLeadTimeGroup,
      getLeadTimeOverride,
      orderVariants,
      productLeadTimeGroups
    ]
  );

  return orderCutOffTime;
};

export default useOrderCutoffTime;
